import type { FormalityRequestType } from "@/types/entities/formality-request";
import { Badge, Button, Icon } from "@elaniintc/goes-design-system";
import dayjs from "dayjs";
import { VeredictCancelRequestModal } from "../cancelation/veredict-cancel-modal";
import { useMemo, useState } from "react";
import { useGetMe } from "@/queries/me";

interface FinishStatusBannerProps {
  formalityCode: string;
  formalityRequest: FormalityRequestType | null | undefined;
  requestGroups?: FormalityRequestType["groups"];
  isCancelled?: boolean;
  isCompleted?: boolean;
  hidden?: boolean;
}

export const FinishStatusBanner = ({
  formalityRequest,
  formalityCode,
  requestGroups,
  isCancelled = false,
  isCompleted = false,
  hidden,
}: FinishStatusBannerProps) => {
  const [open, setOpen] = useState(false);
  const { data: me } = useGetMe();

  const { latestCancellation, isCancelledByRequest, isDenied } = useMemo(() => {
    const cancellation_requests = formalityRequest?.cancellation_requests;
    if (!cancellation_requests || cancellation_requests.length === 0) {
      return {
        latestCancellation: null,
        isCancelledByRequest: false,
        isDenied: false,
      };
    }
    const latest =
      cancellation_requests.at(-1) ?? cancellation_requests[0] ?? null;
    return {
      latestCancellation: latest,
      isCancelledByRequest: latest?.approved_at !== null,
      isDenied: latest?.denied_at !== null,
    };
  }, [formalityRequest]);
  const canUserApprove = useMemo(() => {
    const userSeniorGroups = me?.data.groups?.filter(
      (group) => group.pivot?.seniority === 1,
    );
    return requestGroups?.some((group) =>
      userSeniorGroups?.some((seniorGroup) => seniorGroup.id === group.id),
    );
  }, [me?.data.groups, requestGroups]);

  if (hidden) {
    return null;
  }

  if (isCompleted) {
    const completedDate = dayjs(formalityRequest?.updated_at);
    const completedDateDisplay = completedDate.isValid()
      ? completedDate.format("DD [de] MMMM, YYYY")
      : "";

    return (
      <div className="bg-neutral-object-accent-silent p-2 border border-neutral-quiet flex flex-col gap-1 rounded-lg">
        <Badge size="sm" variant="info" className="shrink-0 grow-0 w-fit">
          Trámite Finalizado
        </Badge>
        <p className="text-body leading-3xl whitespace-pre-line text-justify">
          El proceso ha sido completado. No existen pasos adicionales
          pendientes.
        </p>
        {completedDateDisplay && (
          <span className="text-body-quiet text-size-body-sm text-size-mono-xs">
            {completedDateDisplay}
          </span>
        )}
      </div>
    );
  }

  if (!latestCancellation || (isDenied && !isCancelled)) {
    return null;
  }

  const { reason, requested_at, created_by } = latestCancellation;
  const showCancelledState = isCancelled || isCancelledByRequest;

  return (
    <>
      <div className="bg-neutral-object-accent-silent p-2 border border-neutral-quiet flex flex-col gap-1 rounded-lg">
        <Badge
          size="sm"
          variant={showCancelledState ? "danger" : "warning"}
          className="shrink-0 grow-0 w-fit"
        >
          {showCancelledState
            ? "Trámite Desistido"
            : "Solicitud de desistimiento"}
        </Badge>
        <p className="text-body leading-3xl whitespace-pre-line text-justify">
          {reason || ""}
        </p>
        <div>
          {requested_at && (
            <span className="text-body-quiet text-size-body-sm text-size-mono-xs">
              {dayjs(requested_at).isValid()
                ? dayjs(requested_at).format("DD [de] MMMM, YYYY")
                : ""}
            </span>
          )}{" "}
          {created_by && (
            <>
              <span className="text-size-body-sm text-body-quiet">por</span>{" "}
              <span className="text-size-body-sm text-action">
                {created_by.first_name || ""} {created_by.last_name || ""}
              </span>
            </>
          )}
        </div>
        {!showCancelledState && canUserApprove && (
          <Button
            variant="tertiary"
            size="xs"
            className="shrink-0 gap-1 grow-0 w-fit text-warning"
            onClick={() => {
              setOpen(true);
            }}
          >
            Enviar veredicto
            <Icon type="far" size="sm" name="arrow-up-right" />
          </Button>
        )}
      </div>
      {latestCancellation && (
        <VeredictCancelRequestModal
          isOpen={open}
          onClose={() => setOpen(false)}
          requestCode={formalityCode}
          cancellationId={latestCancellation.id}
          cancellationReason={latestCancellation.reason ?? ""}
        />
      )}
    </>
  );
};
